import type { LangMap } from "./index";

const en = {
  sync: {
    title: "Synced account and homework",
    subtitle: "Sign in with Google or Apple to keep notes, XP and streak on every device. Link your gradebook and Classroom to always have up-to-date homework.",
  },
  title: "Community",
  subtitle: "Follow classmates, challenge each other and share school notes.",
  tabs: { challenge: "Challenge", notes: "Notes", upload: "Upload", privacy: "Privacy" },
  open: "Open community",
  homeCard: { title: "Community", sub: "Challenges, friends and shared notes" },
  privacy: {
    title: "Privacy and safety",
    subtitle: "Decide who can find you and what they can see. No comments or messages exist in Lumen.",
    discoverable: "Appear in student search",
    showStats: "Show my XP, level and streak",
    allowDownloads: "Allow downloading my attachments",
    blockedTitle: "Blocked and muted students",
    blockedEmpty: "You haven't blocked or muted anyone.",
    block: "Block",
    mute: "Mute",
    unblock: "Unblock",
    blockedBadge: "Blocked",
    mutedBadge: "Muted",
    remove: "Remove",
    blocked: "Student blocked",
    muted: "Student muted",
    restored: "Restriction removed",
    savedOk: "Privacy settings saved",
  },
  report: {
    action: "Report",
    title: "Report these notes",
    subtitle: "Tell us what's wrong: a review starts automatically and we'll show you the outcome.",
    details: "Extra details (optional)",
    submit: "Send report",
    sent: "Report sent, thanks.",
    reviewed: "Reviewed: the notes were removed.",
    kept: "Reviewed: the notes respect the rules.",
    already: "You already reported these notes.",
    own: "You can't report your own notes.",
    reasons: {
      not_school: "Not school material",
      spam_or_ads: "Spam or advertising",
      personal_data: "Personal data or contacts",
      unsafe: "Unsafe or offensive content",
      copyright: "Copyrighted book or paid material",
      low_quality: "Empty or unreadable",
    },
    myTitle: "Your reports",
    myEmpty: "No reports sent.",
    status: { open: "Under review", dismissed: "Content kept", actioned: "Content removed" },
    reasonLabel: "Reason",
    err: { not_found: "Notes not found", own_note: "You can't report your own notes.", already_reported: "You already reported these notes." },
  },
  student: {
    anonymous: "Student",
    level: "Level",
    streak: "Streak",
    week: "Min this week",
    followers: "{{count}} followers",
    followingCount: "{{count}} following",
    statsHidden: "This student keeps their stats private.",
    notes: "Shared notes",
    notesEmpty: "No notes shared yet.",
    unavailable: "This profile isn't available.",
  },
  profile: {
    title: "Your public card",
    username: "Username",
    usernamePlaceholder: "e.g. giulia.rossi",
    public: "Visible to other students",
    save: "Save",
    saved: "Profile updated",
    taken: "Username already taken",
    needUsername: "Choose a username to appear in the community.",
    followers: "Followers",
    following: "Following",
  },
  search: {
    people: "Search students by name or username",
    notes: "Search notes by topic, subject or title",
    empty: "No results yet.",
  },
  follow: "Follow",
  unfollow: "Following",
  privateProfile: "This profile is private.",
  board: {
    title: "Weekly challenge",
    subtitle: "You and the people you follow, ranked by XP.",
    xp: "XP",
    level: "Lv {{level}}",
    streak: "{{count}} day streak",
    week: "{{count}} min this week",
    empty: "Follow someone to start challenging each other.",
    you: "You",
  },
  notes: {
    title: "Shared notes",
    scope: { all: "All", following: "Following", saved: "Saved", mine: "Mine" },
    filters: "Filters",
    docType: "Document type",
    level: "School level",
    sort: "Sort by",
    any: "Any",
    sorts: { recent: "Most recent", popular: "Most viewed", saved: "Most saved" },
    docTypes: {
      notes: "Lesson notes", summary: "Summary", exercises: "Exercises",
      formulas: "Formula sheet", slides: "Slides", past_paper: "Past paper",
    },
    results: "{{count}} results",
    unavailable: "These notes are no longer available: they were removed or the author restricted access.",
    downloadDisabled: "The author disabled downloading the attachment. You can still read the notes here.",
    downloadable: "Allow others to download my attachment",
    open: "Open",
    save: "Save",
    unsave: "Saved",
    views: "{{count}} views",
    delete: "Delete",
    deleted: "Note deleted",
    file: "Attachment",
    download: "Download attachment",
    status: { pending: "Under review", rejected: "Not published", approved: "Published" },
    rejectedHint: "Only school study material can be published.",
  },
  upload: {
    title: "Share your notes",
    hint: "Only school or university study material is accepted: an automatic check reviews every upload.",
    noteTitle: "Title",
    subject: "Subject",
    topics: "Topics (comma separated)",
    level: "School level",
    language: "Language",
    description: "Short description",
    content: "Notes text",
    contentPlaceholder: "Paste or write your notes here (min. 120 characters)…",
    fileLabel: "Attach a PDF or image (optional)",
    submit: "Publish",
    publishing: "Checking content…",
    published: "Published! Your notes are now searchable.",
    pending: "Sent for review: we'll publish it once verified.",
    rejected: "Not published: this doesn't look like school study material.",
    rules: "No messages, no contacts, no advertising. Comments and chats are disabled by design.",
    levels: { elementari: "Primary", medie: "Middle school", superiori: "High school", universita: "University" },
  },
};

const it: typeof en = {
  sync: {
    title: "Account e compiti sincronizzati",
    subtitle:
      "Accedi con Google o Apple per ritrovare appunti, XP e streak su ogni dispositivo. Collega il registro e Classroom per avere i compiti sempre aggiornati.",
  },
  title: "Community",
  subtitle: "Segui i compagni, sfidatevi e condividete appunti scolastici.",
  tabs: { challenge: "Sfida", notes: "Appunti", upload: "Carica", privacy: "Privacy" },
  open: "Apri la community",
  homeCard: { title: "Community", sub: "Sfide, amici e appunti condivisi" },
  privacy: {
    title: "Privacy e sicurezza",
    subtitle: "Decidi chi può trovarti e cosa può vedere. In Lumen non esistono commenti né messaggi.",
    discoverable: "Compari nella ricerca studenti",
    showStats: "Mostra XP, livello e serie",
    allowDownloads: "Permetti il download dei miei allegati",
    blockedTitle: "Studenti bloccati e silenziati",
    blockedEmpty: "Non hai bloccato né silenziato nessuno.",
    block: "Blocca",
    mute: "Silenzia",
    unblock: "Sblocca",
    blockedBadge: "Bloccato",
    mutedBadge: "Silenziato",
    remove: "Rimuovi",
    blocked: "Studente bloccato",
    muted: "Studente silenziato",
    restored: "Restrizione rimossa",
    savedOk: "Impostazioni privacy salvate",
  },
  report: {
    action: "Segnala",
    title: "Segnala questi appunti",
    subtitle: "Dicci cosa non va: parte subito una revisione e ti mostriamo l'esito.",
    details: "Dettagli aggiuntivi (facoltativo)",
    submit: "Invia segnalazione",
    sent: "Segnalazione inviata, grazie.",
    reviewed: "Revisione fatta: gli appunti sono stati rimossi.",
    kept: "Revisione fatta: gli appunti rispettano le regole.",
    already: "Hai già segnalato questi appunti.",
    own: "Non puoi segnalare i tuoi appunti.",
    reasons: {
      not_school: "Non è materiale scolastico",
      spam_or_ads: "Spam o pubblicità",
      personal_data: "Dati personali o contatti",
      unsafe: "Contenuto offensivo o non sicuro",
      copyright: "Libro protetto o materiale a pagamento",
      low_quality: "Vuoto o illeggibile",
    },
    myTitle: "Le tue segnalazioni",
    myEmpty: "Nessuna segnalazione inviata.",
    status: { open: "In revisione", dismissed: "Contenuto mantenuto", actioned: "Contenuto rimosso" },
    reasonLabel: "Motivo",
    err: { not_found: "Appunti non trovati", own_note: "Non puoi segnalare i tuoi appunti.", already_reported: "Hai già segnalato questi appunti." },
  },
  student: {
    anonymous: "Studente",
    level: "Livello",
    streak: "Serie",
    week: "Min questa settimana",
    followers: "{{count}} follower",
    followingCount: "{{count}} seguiti",
    statsHidden: "Questo studente tiene private le sue statistiche.",
    notes: "Appunti condivisi",
    notesEmpty: "Nessun appunto condiviso.",
    unavailable: "Questo profilo non è disponibile.",
  },
  profile: {
    title: "La tua scheda pubblica",
    username: "Nome utente",
    usernamePlaceholder: "es. giulia.rossi",
    public: "Visibile agli altri studenti",
    save: "Salva",
    saved: "Profilo aggiornato",
    taken: "Nome utente già in uso",
    needUsername: "Scegli un nome utente per comparire nella community.",
    followers: "Follower",
    following: "Segui",
  },
  search: {
    people: "Cerca studenti per nome o nome utente",
    notes: "Cerca appunti per argomento, materia o titolo",
    empty: "Nessun risultato.",
  },
  follow: "Segui",
  unfollow: "Segui già",
  privateProfile: "Questo profilo è privato.",
  board: {
    title: "Sfida della settimana",
    subtitle: "Tu e le persone che segui, in classifica per XP.",
    xp: "XP",
    level: "Liv {{level}}",
    streak: "{{count}} giorni di fila",
    week: "{{count}} min questa settimana",
    empty: "Segui qualcuno per iniziare a sfidarvi.",
    you: "Tu",
  },
  notes: {
    title: "Appunti condivisi",
    scope: { all: "Tutti", following: "Chi segui", saved: "Salvati", mine: "I miei" },
    filters: "Filtri",
    docType: "Tipo di documento",
    level: "Livello scolastico",
    sort: "Ordina per",
    any: "Tutti",
    sorts: { recent: "Più recenti", popular: "Più visti", saved: "Più salvati" },
    docTypes: {
      notes: "Appunti di lezione", summary: "Riassunto", exercises: "Esercizi",
      formulas: "Formulario", slides: "Slide", past_paper: "Verifica passata",
    },
    results: "{{count}} risultati",
    unavailable: "Questi appunti non sono più disponibili: sono stati rimossi o l'autore ne ha limitato l'accesso.",
    downloadDisabled: "L'autore ha disattivato il download dell'allegato. Puoi comunque leggere gli appunti qui.",
    downloadable: "Permetti agli altri di scaricare il mio allegato",
    open: "Apri",
    save: "Salva",
    unsave: "Salvato",
    views: "{{count}} visualizzazioni",
    delete: "Elimina",
    deleted: "Appunto eliminato",
    file: "Allegato",
    download: "Scarica allegato",
    status: { pending: "In revisione", rejected: "Non pubblicato", approved: "Pubblicato" },
    rejectedHint: "Si possono pubblicare solo materiali di studio scolastici.",
  },
  upload: {
    title: "Condividi i tuoi appunti",
    hint: "Sono ammessi solo materiali scolastici o universitari: un controllo automatico verifica ogni caricamento.",
    noteTitle: "Titolo",
    subject: "Materia",
    topics: "Argomenti (separati da virgola)",
    level: "Livello scolastico",
    language: "Lingua",
    description: "Breve descrizione",
    content: "Testo degli appunti",
    contentPlaceholder: "Incolla o scrivi qui i tuoi appunti (min. 120 caratteri)…",
    fileLabel: "Allega un PDF o un'immagine (facoltativo)",
    submit: "Pubblica",
    publishing: "Controllo contenuto…",
    published: "Pubblicato! I tuoi appunti ora sono ricercabili.",
    pending: "Inviato in revisione: lo pubblicheremo dopo la verifica.",
    rejected: "Non pubblicato: non sembra materiale di studio scolastico.",
    rules: "Niente messaggi, contatti o pubblicità. Commenti e chat sono disattivati per scelta.",
    levels: { elementari: "Elementari", medie: "Medie", superiori: "Superiori", universita: "Università" },
  },
};

const es: typeof en = {
  ...en,
  sync: { title: "Cuenta y tareas sincronizadas", subtitle: "Inicia sesión con Google o Apple para tener apuntes, XP y racha en todos tus dispositivos. Conecta el registro y Classroom para tener las tareas siempre al día." },
  title: "Comunidad",
  subtitle: "Sigue a tus compañeros, retaos y comparte apuntes escolares.",
  tabs: { ...en.tabs, challenge: "Reto", notes: "Apuntes", upload: "Subir" },
  profile: { ...en.profile, title: "Tu ficha pública", username: "Nombre de usuario", public: "Visible para otros estudiantes", save: "Guardar", saved: "Perfil actualizado", taken: "Nombre de usuario ya usado", needUsername: "Elige un nombre de usuario para aparecer en la comunidad.", followers: "Seguidores", following: "Siguiendo" },
  search: { people: "Busca estudiantes por nombre o usuario", notes: "Busca apuntes por tema, materia o título", empty: "Sin resultados." },
  follow: "Seguir",
  unfollow: "Siguiendo",
  board: { ...en.board, title: "Reto semanal", subtitle: "Tú y quienes sigues, ordenados por XP.", level: "Nv {{level}}", streak: "{{count}} días seguidos", week: "{{count}} min esta semana", empty: "Sigue a alguien para empezar a retaros.", you: "Tú" },
  notes: { ...en.notes, title: "Apuntes compartidos", scope: { all: "Todos", following: "Seguidos", saved: "Guardados", mine: "Míos" }, open: "Abrir", save: "Guardar", unsave: "Guardado", views: "{{count}} vistas", delete: "Eliminar", deleted: "Apunte eliminado", download: "Descargar archivo", status: { pending: "En revisión", rejected: "No publicado", approved: "Publicado" }, rejectedHint: "Solo se puede publicar material escolar." },
  upload: { ...en.upload, title: "Comparte tus apuntes", hint: "Solo material escolar o universitario: una revisión automática comprueba cada subida.", noteTitle: "Título", subject: "Materia", topics: "Temas (separados por comas)", level: "Nivel escolar", language: "Idioma", description: "Breve descripción", content: "Texto de los apuntes", contentPlaceholder: "Pega o escribe aquí tus apuntes (mín. 120 caracteres)…", fileLabel: "Adjunta un PDF o imagen (opcional)", submit: "Publicar", publishing: "Comprobando contenido…", published: "¡Publicado! Ya se pueden buscar.", pending: "Enviado a revisión: lo publicaremos tras verificarlo.", rejected: "No publicado: no parece material escolar.", rules: "Sin mensajes, contactos ni publicidad. Comentarios y chats están desactivados.", levels: { elementari: "Primaria", medie: "Secundaria", superiori: "Bachillerato", universita: "Universidad" } },
};

const fr: typeof en = {
  ...en,
  sync: { title: "Compte et devoirs synchronisés", subtitle: "Connecte-toi avec Google ou Apple pour retrouver notes, XP et série sur tous tes appareils. Relie le carnet de notes et Classroom pour des devoirs toujours à jour." },
  title: "Communauté",
  subtitle: "Suis tes camarades, défiez-vous et partagez vos notes scolaires.",
  tabs: { ...en.tabs, challenge: "Défi", notes: "Notes", upload: "Publier" },
  profile: { ...en.profile, title: "Ta fiche publique", username: "Nom d'utilisateur", public: "Visible par les autres élèves", save: "Enregistrer", saved: "Profil mis à jour", taken: "Nom d'utilisateur déjà pris", needUsername: "Choisis un nom d'utilisateur pour apparaître dans la communauté.", followers: "Abonnés", following: "Abonnements" },
  search: { people: "Cherche des élèves par nom ou pseudo", notes: "Cherche des notes par sujet, matière ou titre", empty: "Aucun résultat." },
  follow: "Suivre",
  unfollow: "Suivi",
  board: { ...en.board, title: "Défi de la semaine", subtitle: "Toi et tes abonnements, classés par XP.", level: "Niv {{level}}", streak: "{{count}} jours d'affilée", week: "{{count}} min cette semaine", empty: "Suis quelqu'un pour commencer à vous défier.", you: "Toi" },
  notes: { ...en.notes, title: "Notes partagées", scope: { all: "Toutes", following: "Abonnements", saved: "Enregistrées", mine: "Les miennes" }, open: "Ouvrir", save: "Enregistrer", unsave: "Enregistrée", views: "{{count}} vues", delete: "Supprimer", deleted: "Note supprimée", download: "Télécharger la pièce jointe", status: { pending: "En vérification", rejected: "Non publiée", approved: "Publiée" }, rejectedHint: "Seul le matériel scolaire peut être publié." },
  upload: { ...en.upload, title: "Partage tes notes", hint: "Uniquement du matériel scolaire ou universitaire : une vérification automatique contrôle chaque envoi.", noteTitle: "Titre", subject: "Matière", topics: "Sujets (séparés par des virgules)", level: "Niveau scolaire", language: "Langue", description: "Courte description", content: "Texte des notes", contentPlaceholder: "Colle ou écris tes notes ici (min. 120 caractères)…", fileLabel: "Joindre un PDF ou une image (facultatif)", submit: "Publier", publishing: "Vérification du contenu…", published: "Publié ! Tes notes sont maintenant consultables.", pending: "Envoyé en vérification : publication après contrôle.", rejected: "Non publié : cela ne ressemble pas à du matériel scolaire.", rules: "Pas de messages, contacts ni publicité. Commentaires et chats sont désactivés.", levels: { elementari: "Primaire", medie: "Collège", superiori: "Lycée", universita: "Université" } },
};

const de: typeof en = {
  ...en,
  sync: { title: "Konto und Aufgaben synchronisiert", subtitle: "Melde dich mit Google oder Apple an, um Notizen, XP und Streak auf jedem Gerät zu behalten. Verbinde Klassenbuch und Classroom für stets aktuelle Aufgaben." },
  title: "Community",
  subtitle: "Folge Mitschülern, fordert euch heraus und teilt Schulnotizen.",
  tabs: { ...en.tabs, challenge: "Duell", notes: "Notizen", upload: "Hochladen" },
  profile: { ...en.profile, title: "Deine öffentliche Karte", username: "Benutzername", public: "Für andere Lernende sichtbar", save: "Speichern", saved: "Profil aktualisiert", taken: "Benutzername schon vergeben", needUsername: "Wähle einen Benutzernamen, um in der Community zu erscheinen.", followers: "Follower", following: "Folge ich" },
  search: { people: "Suche Lernende nach Name oder Benutzername", notes: "Suche Notizen nach Thema, Fach oder Titel", empty: "Keine Ergebnisse." },
  follow: "Folgen",
  unfollow: "Folge ich",
  board: { ...en.board, title: "Wochen-Duell", subtitle: "Du und deine Follows, sortiert nach XP.", level: "Lv {{level}}", streak: "{{count}} Tage in Folge", week: "{{count}} Min diese Woche", empty: "Folge jemandem, um euch herauszufordern.", you: "Du" },
  notes: { ...en.notes, title: "Geteilte Notizen", scope: { all: "Alle", following: "Follows", saved: "Gespeichert", mine: "Meine" }, open: "Öffnen", save: "Speichern", unsave: "Gespeichert", views: "{{count}} Aufrufe", delete: "Löschen", deleted: "Notiz gelöscht", download: "Anhang herunterladen", status: { pending: "In Prüfung", rejected: "Nicht veröffentlicht", approved: "Veröffentlicht" }, rejectedHint: "Es darf nur schulisches Lernmaterial veröffentlicht werden." },
  upload: { ...en.upload, title: "Teile deine Notizen", hint: "Nur Schul- oder Unimaterial: Jede Datei wird automatisch geprüft.", noteTitle: "Titel", subject: "Fach", topics: "Themen (mit Komma trennen)", level: "Schulstufe", language: "Sprache", description: "Kurze Beschreibung", content: "Notiztext", contentPlaceholder: "Füge deine Notizen hier ein (mind. 120 Zeichen)…", fileLabel: "PDF oder Bild anhängen (optional)", submit: "Veröffentlichen", publishing: "Inhalt wird geprüft…", published: "Veröffentlicht! Deine Notizen sind jetzt auffindbar.", pending: "Zur Prüfung gesendet: Veröffentlichung nach Kontrolle.", rejected: "Nicht veröffentlicht: kein schulisches Lernmaterial.", rules: "Keine Nachrichten, Kontakte oder Werbung. Kommentare und Chats sind bewusst deaktiviert.", levels: { elementari: "Grundschule", medie: "Mittelstufe", superiori: "Oberstufe", universita: "Universität" } },
};

const pt: typeof en = {
  ...en,
  sync: { title: "Conta e tarefas sincronizadas", subtitle: "Entre com Google ou Apple para manter anotações, XP e sequência em todos os dispositivos. Conecte o registro e o Classroom para ter tarefas sempre atualizadas." },
  title: "Comunidade",
  subtitle: "Segue colegas, desafiem-se e partilhem apontamentos escolares.",
  tabs: { ...en.tabs, challenge: "Desafio", notes: "Apontamentos", upload: "Carregar" },
  profile: { ...en.profile, title: "O teu cartão público", username: "Nome de utilizador", public: "Visível para outros estudantes", save: "Guardar", saved: "Perfil atualizado", taken: "Nome de utilizador já usado", needUsername: "Escolhe um nome de utilizador para apareceres na comunidade.", followers: "Seguidores", following: "A seguir" },
  search: { people: "Procura estudantes por nome ou utilizador", notes: "Procura apontamentos por tema, disciplina ou título", empty: "Sem resultados." },
  follow: "Seguir",
  unfollow: "A seguir",
  board: { ...en.board, title: "Desafio da semana", subtitle: "Tu e quem segues, por XP.", level: "Nv {{level}}", streak: "{{count}} dias seguidos", week: "{{count}} min esta semana", empty: "Segue alguém para começarem a desafiar-se.", you: "Tu" },
  notes: { ...en.notes, title: "Apontamentos partilhados", scope: { all: "Todos", following: "Quem sigo", saved: "Guardados", mine: "Meus" }, open: "Abrir", save: "Guardar", unsave: "Guardado", views: "{{count}} visualizações", delete: "Eliminar", deleted: "Apontamento eliminado", download: "Descarregar anexo", status: { pending: "Em revisão", rejected: "Não publicado", approved: "Publicado" }, rejectedHint: "Só é permitido material escolar." },
  upload: { ...en.upload, title: "Partilha os teus apontamentos", hint: "Só material escolar ou universitário: cada envio passa por verificação automática.", noteTitle: "Título", subject: "Disciplina", topics: "Temas (separados por vírgulas)", level: "Nível escolar", language: "Idioma", description: "Breve descrição", content: "Texto dos apontamentos", contentPlaceholder: "Cola ou escreve aqui (mín. 120 caracteres)…", fileLabel: "Anexar PDF ou imagem (opcional)", submit: "Publicar", publishing: "A verificar conteúdo…", published: "Publicado! Já pode ser encontrado.", pending: "Enviado para revisão: publicamos após verificação.", rejected: "Não publicado: não parece material escolar.", rules: "Sem mensagens, contactos ou publicidade. Comentários e chats estão desativados.", levels: { elementari: "Primária", medie: "Básico", superiori: "Secundário", universita: "Universidade" } },
};

export const community: LangMap = { en, it, es, fr, de, pt };
